import {
  getAccountsByUser,
  getTradesByUser,
  useQuery,
} from "wasp/client/operations";

export interface OnboardingStep {
  id: "account" | "trade" | "session-detail" | "prop-firm-manager";
  emoji: string;
  titleKey: "step1Title" | "step2Title" | "step3Title" | "step4Title";
  descriptionKey: "step1Description" | "step2Description" | "step3Description" | "step4Description";
  ctaKey: "step1Cta" | "step2Cta" | "step3Cta" | "step4Cta";
  celebrationKey: "step1Celebration" | "step2Celebration" | "step3Celebration" | "step4Celebration";
  completed: boolean;
  href: string;
}

export interface OnboardingStatus {
  steps: OnboardingStep[];
  completedCount: number;
  allCompleted: boolean;
  isLoading: boolean;
}

export function useOnboardingStatus(userId: string | undefined): OnboardingStatus {
  const isAuthenticated = !!userId;

  const { data: accountsStatusData, isLoading: l1 } = useQuery(
    getAccountsByUser,
    undefined,
    { enabled: isAuthenticated }
  );
  const accounts = (accountsStatusData as any)?.accounts ?? [];
  const { data: trades, isLoading: l2 } = useQuery(
    getTradesByUser,
    undefined,
    { enabled: isAuthenticated }
  );

  const isLoading = l1 || l2;

  const sessionVisited = userId
    ? localStorage.getItem(`onboarding_sessionDetailVisited_${userId}`) === "true"
    : false;

  const propFirmVisited = userId
    ? localStorage.getItem(`onboarding_propFirmManagerVisited_${userId}`) === "true"
    : false;

  const steps: OnboardingStep[] = [
    {
      id: "account",
      emoji: "🏦",
      titleKey: "step1Title",
      descriptionKey: "step1Description",
      ctaKey: "step1Cta",
      celebrationKey: "step1Celebration",
      completed: (accounts?.length ?? 0) > 0,
      href: "/trading-accounts",
    },
    {
      id: "trade",
      emoji: "📈",
      titleKey: "step2Title",
      descriptionKey: "step2Description",
      ctaKey: "step2Cta",
      celebrationKey: "step2Celebration",
      completed: ((trades as any)?.trades?.length ?? 0) > 0,
      href: "/trading-journal",
    },
    {
      id: "session-detail",
      emoji: "⏱️",
      titleKey: "step3Title",
      descriptionKey: "step3Description",
      ctaKey: "step3Cta",
      celebrationKey: "step3Celebration",
      completed: sessionVisited,
      href: "/trading-sessions",
    },
    {
      id: "prop-firm-manager",
      emoji: "🏆",
      titleKey: "step4Title",
      descriptionKey: "step4Description",
      ctaKey: "step4Cta",
      celebrationKey: "step4Celebration",
      completed: propFirmVisited,
      href: "/prop-firm-manager",
    },
  ];

  const completedCount = steps.filter((s) => s.completed).length;
  const allCompleted = completedCount === 4;

  return { steps, completedCount, allCompleted, isLoading };
}
